import { createContext, useState, useEffect, type ReactNode } from 'react';

type ThemeMode = 'light' | 'dark' | 'system';

export interface ThemeContextValue {
  mode: ThemeMode;
  isDark: boolean;
  setMode: (mode: ThemeMode) => void;
}

export const ThemeContext = createContext<ThemeContextValue | undefined>(
  undefined
);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const getInitialMode = (): ThemeMode => {
    const cookie = document.cookie
      .split('; ')
      .find((c) => c.startsWith('theme_preference='));
    return (cookie?.split('=')[1] as ThemeMode) || 'light';
  };

  const [mode, setModeState] = useState<ThemeMode>(getInitialMode);

  const isDark =
    mode === 'dark' ||
    (mode === 'system' &&
      window.matchMedia('(prefers-color-scheme: dark)').matches);

  const setMode = (newMode: ThemeMode) => {
    setModeState(newMode);
    document.cookie = `theme_preference=${newMode};path=/;max-age=${365 * 24 * 60 * 60}`;
  };

  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDark);
  }, [isDark]);

  // Listen for system preference changes
  useEffect(() => {
    if (mode !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () =>
      document.documentElement.classList.toggle('dark', mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [mode]);

  return (
    <ThemeContext.Provider value={{ mode, isDark, setMode }}>
      {children}
    </ThemeContext.Provider>
  );
}
